import React, { FC, useContext, useMemo } from "react";
import {
  Inline,
  View,
  useAppContext,
  useClientContext,
  useNavigation,
  ViewHeader,
} from "@data-root/react";
import { ScreenProps } from "../typings";
import { AppSpecifier, DetailView as IDetailView } from "@data-root/types";
import { Empty, Layout } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Detail } from "../components/inline/Detail";
import { UseRowContext, useAsyncCallback, useRow } from "@data-root/react";
import FloatButton from "../components/FloatButton";
import { core } from "..";
import { ErrorView } from "@data-root/error-boundary";

const { Content } = Layout;

const create = async (appSpecifier: AppSpecifier, tableId: string) => {
  const row = await core.emptyRow(
    appSpecifier,
    tableId,
    undefined,
    undefined,
    undefined,
    undefined
  );

  const delta = await core.add(appSpecifier, tableId, row);

  return delta;
};

export const DetailView: FC<ScreenProps<IDetailView>> = ({ view }) => {
  const { navigation } = useNavigation();

  const rowId = navigation.getParam("rowId");
  const tabId = navigation.getParam("tabId");

  const { tableId, options, noAdd, noEdit, noDelete } = view;

  const { core } = useClientContext();
  const { appSpecifier, styles, getTable } = useAppContext();

  const table = useMemo(() => getTable(tableId), [getTable, tableId]);

  const useRowHook = useRow(core, appSpecifier, tableId, rowId);
  const { getContext, loaded, error } = useRowHook;

  const onDelete = useAsyncCallback(async () => {
    await core.delete(appSpecifier, tableId, rowId!);
    navigation.goBack();
  }, [core, appSpecifier, tableId, rowId, navigation]);

  const onEdit = useAsyncCallback(async () => {
    const onEdit = await core.evaluateFormula<string>(options.onEdit, {
      appId: appSpecifier.appId,
      deploymentId: appSpecifier.deploymentId,
    });

    if (onEdit) navigation.navigate(onEdit, { rowId, tabId });
  }, [
    core,
    options.onEdit,
    appSpecifier.appId,
    appSpecifier.deploymentId,
    navigation,
    rowId,
    tabId,
  ]);

  const onNew = useAsyncCallback(async () => {
    const context = getContext();

    const { rowId } = await create(appSpecifier, view.tableId);
    const onNew = await core.evaluateFormula<string>(options.onNew, context);
    if (onNew) navigation.navigate(onNew, { rowId });
  }, [getContext, appSpecifier, view.tableId, core, options.onNew, navigation]);

  const floatButtons = useMemo(() => {
    const buttons = [];

    if (!noDelete)
      buttons.push(
        <FloatButton
          key="delete"
          onClick={onDelete}
          icon={
            <DeleteOutlined
              style={{
                color: styles.darkTintColor,
              }}
            />
          }
        />
      );

    if (!noEdit)
      buttons.push(
        <FloatButton
          key="edit"
          onClick={onEdit}
          icon={
            <EditOutlined
              style={{
                color: styles.darkTintColor,
              }}
            />
          }
        />
      );

    if (!noAdd)
      buttons.push(
        <FloatButton
          key="new"
          onClick={onNew}
          icon={
            <PlusOutlined
              style={{
                color: styles.darkTintColor,
              }}
            />
          }
        />
      );

    return buttons;
  }, [noAdd, noDelete, noEdit, onDelete, onEdit, onNew, styles.darkTintColor]);

  if (error) return <ErrorView error={error} />;

  if (!rowId)
    return (
      <View style={{ height: "100%" }}>
        <Empty style={{ margin: "auto" }} />
      </View>
    );

  return (
    <UseRowContext.Provider value={useRowHook}>
      <Inline table={table} rowId={rowId} view={view}>
        <Layout>
          <ViewHeader loaded={loaded} view={view} getContext={getContext} />

          <Content style={style}>
            <Detail getContext={getContext} loaded={loaded} />
          </Content>

          {floatButtons.length > 0 && (
            <FloatButton.Group shape="square">{floatButtons}</FloatButton.Group>
          )}
        </Layout>
      </Inline>
    </UseRowContext.Provider>
  );
};

const style = {
  overflow: "auto",
  padding: 10,
  paddingTop: 0,
  paddingBottom: 100,
};
